import { type RenderLink, Tabs } from '@pms-digital/design-system';

/**
 * The tab strip of the wide layout (refonte modulaire §9.1; maquette
 * `OngletHistorique.dc.html`).
 *
 * §9.1 gives the experience's editor three tabs - Timeline, Persona,
 * Historique - and the third one is the layer's: « l'historique, "utilisé
 * dans" et le retrait passent dans ce troisième onglet ». So this file draws
 * the strip and nothing else; which panel a tab shows is decided by
 * `versioned-editor.tsx`, and the tabs an object declares are its own.
 *
 * ## The package `Tabs`, and the URL still decides
 *
 * The strip is `Tabs` of `@pms-digital/design-system`: 48 px tabs underlined in
 * bleu nuit, a `tablist` whose arrows move the focus. The selected tab travels in
 * the URL (`?tab=`), so a reload, a back button or a pasted link land on the same
 * panel - each tab is a DESTINATION, which is why every item is given an `href`
 * (only a key here, `#<id>`): that puts `Tabs` in its LINK régime, where the
 * arrows move the focus without selecting and the active tab is the one `value`
 * names. The element each tab renders is still this file's (`renderLink`), a
 * button carrying its `data-testid` and `aria-current="page"` - the two marks the
 * e2e specs and the captures read - beside the `role="tab"` `Tabs` gives it.
 *
 * ## Buttons and not links, for the same reason `onView` is a callback
 *
 * The layer is blind to the route it sits on: TanStack types `to` and `search`
 * against the route tree, so a `<Link>` written here could not typecheck
 * against the params of whichever screen mounts it. The selection therefore
 * leaves by a callback and the CONSUMER writes the navigation, exactly as
 * `?version=` already does through `onView` / `onCloseView`.
 *
 * The label is « Sections » and not « Sections de l'expérience » of the
 * maquette: the noun would have to be templated with its article, and
 * `VersionedWording` refuses that by construction (« un article et un accord ne
 * sont pas une concaténation »).
 */

export interface VersionedTabItem {
  /** What `?tab=` carries: « timeline ». */
  readonly id: string;
  /** What the strip shows: « Timeline ». */
  readonly label: string;
}

export interface VersionedTabsProps {
  /** The `data-testid` prefix of the object: « experience » (`VersionedWording.entity`). */
  entity: string;
  /** The object's own tabs, then « Historique » - the order they are read in. */
  tabs: readonly VersionedTabItem[];
  /** The id of the tab being shown, already resolved against the list. */
  active: string;
  onSelect: (id: string) => void;
}

/** The key `Tabs` carries for a tab: never followed, only read back by `renderLink`. */
const KEY_PREFIX = '#';

export function VersionedTabs({ entity, tabs, active, onSelect }: VersionedTabsProps) {
  const renderTab: RenderLink = ({ href, className, children }) => {
    const id = href.slice(KEY_PREFIX.length);
    return (
      <button
        type="button"
        className={className}
        data-testid={`${entity}-onglet-${id}`}
        // `page` and not `true`: the panel it opens is addressable, and
        // `true` is what a step or a date in a set would carry.
        aria-current={id === active ? 'page' : undefined}
        onClick={() => onSelect(id)}
      >
        {children}
      </button>
    );
  };
  // The mark rides an envelope: `Tabs` takes no `data-testid`.
  return (
    <div data-testid={`${entity}-onglets`}>
      <Tabs
        label="Sections"
        value={active}
        items={tabs.map((tab) => ({
          value: tab.id,
          label: tab.label,
          href: `${KEY_PREFIX}${tab.id}`,
        }))}
        renderLink={renderTab}
      />
    </div>
  );
}
